import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';

import { ReactComponent as CloseIcon } from '/src/images/icons/close.svg';

import { useUser } from '../../../context/UserContext';
import { dateRangeMedWithZoneOffset } from '../../../helpers/dates';
import { isBrowser } from '../../../helpers/is-browser';
import { clearUrlParams, setLocalStorageItem } from '../../../helpers/utils';
import { useGetLibraryConstantsDictionary } from '../../../hooks/use-get-library-constants-dictionary';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { IContentfulImage } from '../../../interfaces/contentful-image.interface';
import { IBloackAppDownloadPanel } from '../../../queries/fragments/blockAppDownloadPanel';
import {
  ILibraryMeetingPlace,
  ILibraryMeetingPlaceMinified,
} from '../../../queries/fragments/libraryMeetingPlace';
import { ILibraryOpeningHours } from '../../../queries/fragments/libraryOpeningHours';
import { ILibraryOpeningHoursException } from '../../../queries/fragments/libraryOpeningHoursException';
import { BlockWrapper } from '../../block-wrapper/block-wrapper';
import { BlockSections } from '../../blocks/block-sections';
import { RenderBlocks } from '../../blocks/render-blocks';
import ImageWrapper from '../../image-wrapper/Image-wrapper';
import { RichText } from '../../rich-text/rich-text';
import { Share } from '../../share/share';
import Backdrop from '../../ui/backdrop/backdrop';
import Button from '../../ui/button/button';
import { SideModal } from '../../ui/side-modal/side-modal';
import { SideModalBody } from '../../ui/side-modal/side-modal-body';
import MobileAppLinks from '../mobile-app-links/mobile-app-links';
import { getCompleteData } from '../special-offer-redemption/offer-redemption-api-helper';
import { ScanQRCode } from '../special-offer-redemption/scan-qr-code';
import { SpecialOfferRedemtion } from '../special-offer-redemption/special-offer-redemption';
import { PageComponentMainBottomBorder } from './page-component-main-bottom-border';
import { PageComponentMainHeader } from './page-component-main-header';

interface MainBlock {
  header: string;
  caption?: string;
  typeTag?: string;
  typeTagClass?: string;
  appDownloadPanelReference?: IBloackAppDownloadPanel;
  panelclasses?: string;
  validFrom?: string;
  validTo?: string;
  node_locale: string;
  showSocialShare: boolean;
  logo?: IContentfulImage;
  meetingPlaceOpeningHours?: {
    openingHours?: ILibraryOpeningHours;
    openingHoursExceptions?: ILibraryOpeningHoursException[];
    timezone?: string;
  };
  tags?: string[];
  offerRedemptionMechanism?: string;
  offerRedemptionDetails?: {
    internalName: string;
    richText: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  };
  typeOfOffer?: string;
  contentful_id?: string;
  tenantName?: string;
  meetingPlaceHomePage?: ILibraryMeetingPlaceMinified;
}
interface MainBlockRichText extends MainBlock {
  blocks?: BlockSections[];
  richText: RenderRichTextData<ContentfulRichTextGatsbyReference>;
}
interface MainBlockBlocks extends MainBlock {
  richText?: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  blocks: BlockSections[];
}

export const PageComponentMain = ({
  header,
  caption,
  typeTag,
  typeTagClass,
  appDownloadPanelReference,
  panelclasses,
  richText,
  blocks,
  validFrom,
  validTo,
  node_locale,
  showSocialShare,
  logo,
  meetingPlaceOpeningHours,
  tags,
  offerRedemptionDetails,
  offerRedemptionMechanism = '',
  typeOfOffer,
  contentful_id,
  tenantName,
  meetingPlaceHomePage,
}: MainBlockRichText | MainBlockBlocks) => {
  const [openRightDrawer, setOpenRightDrawer] = useState<boolean>(false);
  const [isOfferRedemptionMobileViewOpen, setIsOfferRedemptionMobileViewOpen] =
    useState<boolean>(false);
  const [showFullScreenQR, setShowFullScreenQR] = useState(false);
  const [isQRCodeScanned, setIsQRCodeScanned] = useState(false);
  const [encryptedScannedText, setEncryptedScannedText] = useState('');
  const [offerBookingData, setOfferBookingData] = useState([]);
  const [showClaimLoader, setShowClaimLoader] = useState(false);

  const { user } = useUser();
  const user_token = Cookies.get('user_token');
  const isMobile = useIsMobile();

  const dictionary = useGetLibraryConstantsDictionary(node_locale);

  const showSpecialOfferRedemption =
    process.env.GATSBY_SHOW_SPECIAL_OFFER_REDEMPTION == 'true' ||
    process.env.SHOW_SPECIAL_OFFER_REDEMPTION == 'true';

  useEffect(() => {
    isOfferRedemptionMobileViewOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [isOfferRedemptionMobileViewOpen]);

  useEffect(() => {
    const getMPData = async () => {
      try {
        setShowClaimLoader(true);
        const offerData = await getCompleteData(
          {
            id: meetingPlaceHomePage?.businessUnitCode,
          },
          {
            'X-Current-Language': node_locale,
            'X-Preferred-Language': node_locale,
          },
        );
        setOfferBookingData(
          offerData.offer.filter((item: any) => item.id == contentful_id),
        );
        setShowClaimLoader(false);
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch user:', err);
      } finally {
        setShowClaimLoader(false);
      }
    };
    if (showSpecialOfferRedemption) {
      getMPData();
    }
  }, []);

  useEffect(() => {
    if (isBrowser) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('modal') === 'claim-offer') {
        if (user_token && user && offerRedemptionMechanism == 'Dynamic') {
          setShowFullScreenQR(true);
        } else if (user_token && user && offerRedemptionMechanism == 'Static') {
          if (isMobile) {
            setIsOfferRedemptionMobileViewOpen(true);
          } else {
            setOpenRightDrawer(true);
          }
        }
      }
    }
  }, [user_token, user, isMobile]);

  const claimOffer = () => {
    if (isBrowser) {
      const params = new URLSearchParams(window.location.search);
      params.set('modal', 'claim-offer');
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      setLocalStorageItem('current_path', newUrl);
      window.history.pushState({}, '', newUrl);
    }
    if (user_token && user && offerRedemptionMechanism == 'Dynamic') {
      setShowFullScreenQR(true);
    } else {
      if (isMobile) {
        setIsOfferRedemptionMobileViewOpen(!isOfferRedemptionMobileViewOpen);
      } else {
        setOpenRightDrawer(!openRightDrawer);
      }
    }
  };

  const handleOfferRedemptionMobileView = () => {
    setIsOfferRedemptionMobileViewOpen(!isOfferRedemptionMobileViewOpen);
  };

  const handleQRCodeScanned = (text: string) => {
    setEncryptedScannedText(text);
    setShowFullScreenQR(false);
    setIsQRCodeScanned(true);
    setOpenRightDrawer(true);
    if (isMobile) {
      setIsOfferRedemptionMobileViewOpen(true);
    }
  };

  return (
    <>
      <BlockWrapper mode="box" className="pb-0 lg:mb-5 lg:pb-20">
        <PageComponentMainBottomBorder />
        {logo && (
          <ImageWrapper
            gatsbyImageData={logo?.gatsbyImageData}
            file={logo?.file}
            svg={logo?.svg}
            alt={logo?.description ? logo.description : ''}
            className="h-12 w-12 lg:h-16 lg:w-16 mb-8 rounded-md bg-brand-white"
            imgClassName="!object-contain"
          />
        )}
        <PageComponentMainHeader header={header} caption={caption} />
        {typeTag && (
          <div
            className={`mt-2 font-title text-headings font-bold ${typeTagClass} text-text-sticker-blue`}
          >
            {typeTag}
          </div>
        )}
        {validTo && validFrom && (
          <div className="my-6 text-headings text-neutral-grey-700">
            {dateRangeMedWithZoneOffset(validFrom, validTo, node_locale)}
          </div>
        )}
        {showSpecialOfferRedemption &&
          typeOfOffer == 'specialOffer' &&
          ((offerBookingData.length > 0 &&
            offerBookingData.some((item: any) => item?.bookingDetails) &&
            offerRedemptionMechanism == 'Dynamic') ||
            offerRedemptionMechanism == 'Static') &&
          !showClaimLoader && (
            <Button
              variant="primary"
              className="w-[42%] md:w-[35%] lg:w-[30%] text-center text-neutral-grey-700 mt-4 mb-8"
              onClick={claimOffer}
            >
              {dictionary?.claimOfferButtonText}
            </Button>
          )}
        {showSpecialOfferRedemption &&
          typeOfOffer == 'specialOffer' &&
          showClaimLoader && (
            <div className="w-[42%] h-[44px] md:w-[35%] lg:w-[30%] mt-4 mb-8 rounded-full bg-accent-primary-normal hover:bg-accent-primary-dark active:bg-brand-yellow-3 disabled:bg-neutral-grey-200 disabled:text-neutral-grey-300 animate-pulse"></div>
          )}
        {tags && (
          <div className="mb-4">
            {tags?.map((tag, index) => {
              return (
                <span
                  key={index}
                  className="border-2 border-neutral-grey-300 py-1 px-2 mr-2 rounded-md text-headingxs text-neutral-grey-700"
                >
                  {tag}
                </span>
              );
            })}
          </div>
        )}
        {appDownloadPanelReference && (
          <MobileAppLinks
            downloadPanel={appDownloadPanelReference}
            panelclasses={panelclasses}
          />
        )}
        {richText && <RichText richText={richText} />}
        {blocks && (
          <RenderBlocks
            blocks={blocks}
            node_locale={node_locale}
            meetingPlaceOpeningHours={meetingPlaceOpeningHours}
          />
        )}
        {showSocialShare && (
          <Share className="absolute -bottom-2 right-0 lg:bottom-8 lg:right-20 xl:right-24" />
        )}
      </BlockWrapper>
      {isOfferRedemptionMobileViewOpen &&
        showSpecialOfferRedemption &&
        isMobile && (
          <>
            <Backdrop
              handleOpen={handleOfferRedemptionMobileView}
              showButtons={false}
              className="!min-h-[250px]"
            >
              <SpecialOfferRedemtion
                node_locale={node_locale}
                offerRedemptionDetails={offerRedemptionDetails}
                offerRedemptionMechanism={offerRedemptionMechanism}
                handleClose={() => {
                  setIsOfferRedemptionMobileViewOpen(
                    !isOfferRedemptionMobileViewOpen,
                  );
                  if (isBrowser) {
                    clearUrlParams();
                    localStorage.removeItem('current_path');
                    setIsQRCodeScanned(false);
                  }
                }}
                isQRCodeScanned={isQRCodeScanned}
                scannedText={encryptedScannedText}
                contentful_id={contentful_id}
                tenantDetails={{
                  offerName: header,
                  tenantName: tenantName,
                  tenantLogo: logo,
                }}
                meetingPlaceHomePage={meetingPlaceHomePage}
                offerBookingData={offerBookingData}
              />
              <button
                className="absolute -top-20 right-4 z-[200] grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all hover:bg-brand-grey-2 focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
                onClick={() => {
                  setIsOfferRedemptionMobileViewOpen(false);
                  if (isBrowser) {
                    clearUrlParams();
                  }
                  localStorage.removeItem('current_path');
                  setIsQRCodeScanned(false);
                }}
              >
                <CloseIcon />
              </button>
            </Backdrop>
          </>
        )}
      {!isMobile && showSpecialOfferRedemption && (
        <SideModal
          isOpen={openRightDrawer}
          handleClose={() => {
            setOpenRightDrawer(!openRightDrawer);
            if (isBrowser) {
              clearUrlParams();
              localStorage.removeItem('current_path');
              setIsQRCodeScanned(false);
            }
          }}
        >
          <SideModalBody>
            <SpecialOfferRedemtion
              node_locale={node_locale}
              offerRedemptionDetails={offerRedemptionDetails}
              offerRedemptionMechanism={offerRedemptionMechanism}
              handleClose={() => {
                setOpenRightDrawer(!openRightDrawer);
                if (isBrowser) {
                  clearUrlParams();
                  localStorage.removeItem('current_path');
                  setIsQRCodeScanned(false);
                }
              }}
              isQRCodeScanned={isQRCodeScanned}
              scannedText={encryptedScannedText}
              contentful_id={contentful_id}
              tenantDetails={{
                offerName: header,
                tenantName: tenantName,
                tenantLogo: logo,
              }}
              offerBookingData={offerBookingData}
              meetingPlaceHomePage={meetingPlaceHomePage}
            />
          </SideModalBody>
        </SideModal>
      )}
      {showFullScreenQR &&
        showSpecialOfferRedemption &&
        offerRedemptionMechanism == 'Dynamic' && (
          <ScanQRCode
            onScanned={handleQRCodeScanned}
            onClose={() => {
              setShowFullScreenQR(false);
              if (isBrowser) {
                clearUrlParams();
                localStorage.removeItem('current_path');
              }
            }}
            node_locale={node_locale}
          />
        )}
    </>
  );
};
